<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type CSSProperties, type PropType } from 'vue'
import PreviewGuideOverlay from '@/components/workspace/PreviewGuideOverlay.vue'
import PreviewSurface from '@/components/workspace/PreviewSurface.vue'
import { getCachedImageResource } from '@/features/preview/imageResourceCache'
import type { PreviewGuideCoordinate } from '@/features/preview/previewGuide'

export type PreviewDrawItem = {
  key: string | number
  kind: 'image' | 'text' | 'rect'
  style?: CSSProperties
  x?: number
  y?: number
  width?: number
  height?: number
  imageUrl?: string
  text?: string
  textColor?: string
  fontSize?: number
  fontFamily?: string
  fontWeight?: string | number
  textAlign?: CanvasTextAlign
  verticalAlign?: 'top' | 'center' | 'bottom'
  lineHeight?: number
  padding?: number
  fillColor?: string
  strokeColor?: string
  lineWidth?: number
  dash?: number[]
  placeholderText?: string
  placeholderFillColor?: string
  placeholderStrokeColor?: string
  onImageLoad?: (payload: { url: string; width: number; height: number }) => void
}

export type PreviewHotspotItem = {
  key: string | number
  tag?: string
  class?: string
  style?: CSSProperties
  ariaLabel?: string
  onPointerdown?: (event: PointerEvent) => void
  onKeyboardMove?: (delta: { dx: number; dy: number }) => void
  onKeyboardResize?: (delta: { dw: number; dh: number }) => void
  stopPointerDown?: boolean
  preventPointerDown?: boolean
  dragging?: boolean
  draggingClass?: string
  resizeHandle?: {
    tag?: string
    class?: string
    style?: CSSProperties
    ariaLabel?: string
    onPointerdown?: (event: PointerEvent) => void
    stopPointerDown?: boolean
    preventPointerDown?: boolean
  }
}

const props = defineProps({
  hostClass: { type: [String, Array, Object] as PropType<any>, default: '' },
  stageClass: { type: [String, Array, Object] as PropType<any>, default: '' },
  designWidth: { type: Number, default: 0 },
  designHeight: { type: Number, default: 0 },
  scaleMultiplier: { type: Number, default: 1 },
  zoomable: { type: Boolean, default: false },
  zoomStep: { type: Number, default: 0.25 },
  backgroundUrl: { type: String, default: '' },
  backgroundEmptyText: { type: String, default: '' },
  drawItems: { type: Array as PropType<PreviewDrawItem[]>, default: () => [] },
  hotspotItems: { type: Array as PropType<PreviewHotspotItem[]>, default: () => [] },
  guideActive: { type: Boolean, default: false },
})

const emit = defineEmits<{
  stageResize: [{ width: number; height: number }]
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const stageDisplaySize = ref({ width: 0, height: 0 })
const activeHotspotKey = ref<string | number | null>(null)
const imageMap = new Map<string, HTMLImageElement | null>()
const requestedImageUrls = new Set<string>()
const notifiedImageKeys = new Set<string>()
let renderFrameHandle = 0

const stageScale = computed(() => {
  if (props.designWidth <= 0 || props.designHeight <= 0) return 1
  if (stageDisplaySize.value.width <= 0 || stageDisplaySize.value.height <= 0) return 1
  return Math.min(stageDisplaySize.value.width / props.designWidth, stageDisplaySize.value.height / props.designHeight)
})
const activeGuideCoordinate = computed<PreviewGuideCoordinate | null>(() => {
  if (!props.guideActive) return null
  const active = props.hotspotItems.find((item) => item.key === activeHotspotKey.value)
  if (!active) return null
  return {
    x: parsePixel(active.style?.left),
    y: parsePixel(active.style?.top),
  }
})
function normalizeUrl(url: string) {
  return String(url || '').trim()
}

function parsePixel(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return 0
}

function resolveItemFrame(item: PreviewDrawItem) {
  return {
    x: item.x ?? parsePixel(item.style?.left),
    y: item.y ?? parsePixel(item.style?.top),
    width: Math.max(0, item.width ?? parsePixel(item.style?.width)),
    height: Math.max(0, item.height ?? parsePixel(item.style?.height)),
  }
}

function resolveItemTextColor(item: PreviewDrawItem) {
  return item.textColor || String(item.style?.color || '#ffffff')
}

function resolveItemFontSize(item: PreviewDrawItem) {
  return Math.max(10, item.fontSize ?? (parsePixel(item.style?.fontSize) || 14))
}

function resolveItemFontFamily(item: PreviewDrawItem) {
  return String(item.fontFamily || item.style?.fontFamily || 'sans-serif')
}

function resolveItemFontWeight(item: PreviewDrawItem) {
  return String(item.fontWeight || item.style?.fontWeight || '400')
}

function scheduleRender() {
  if (renderFrameHandle) {
    cancelAnimationFrame(renderFrameHandle)
  }
  renderFrameHandle = requestAnimationFrame(() => {
    renderFrameHandle = 0
    renderCanvas()
  })
}

function maybeLoadImage(itemKey: string | number, url: string, callback?: PreviewDrawItem['onImageLoad']) {
  const target = normalizeUrl(url)
  if (!target) return null
  if (imageMap.has(target)) {
    const image = imageMap.get(target)
    if (image && callback) {
      const notifyKey = `${itemKey}|${target}`
      if (!notifiedImageKeys.has(notifyKey)) {
        notifiedImageKeys.add(notifyKey)
        callback({
          url: target,
          width: image.naturalWidth || image.width || 0,
          height: image.naturalHeight || image.height || 0,
        })
      }
    }
    return image
  }
  if (!requestedImageUrls.has(target)) {
    requestedImageUrls.add(target)
    void getCachedImageResource(target)
      .then((image) => {
        imageMap.set(target, image)
        scheduleRender()
      })
      .catch(() => {
        imageMap.set(target, null)
        scheduleRender()
      })
  }
  return null
}

function drawPlaceholder(ctx: CanvasRenderingContext2D, item: PreviewDrawItem, text: string) {
  const { x, y, width, height } = resolveItemFrame(item)
  ctx.save()
  ctx.fillStyle = item.placeholderFillColor || '#2b3038'
  ctx.fillRect(x, y, width, height)
  ctx.strokeStyle = item.placeholderStrokeColor || '#6fb6ff'
  ctx.lineWidth = item.lineWidth || 1
  ctx.setLineDash(item.dash || [4, 4])
  ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, width - 1), Math.max(0, height - 1))
  ctx.setLineDash([])
  ctx.fillStyle = '#cbd5e1'
  ctx.font = `500 14px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, x + (width / 2), y + (height / 2), Math.max(0, width - 8))
  ctx.restore()
}

function drawMultilineText(ctx: CanvasRenderingContext2D, item: PreviewDrawItem) {
  const { x, y, width, height } = resolveItemFrame(item)
  const text = String(item.text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\|n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\/n/g, '\n')
  if (!text) return
  const fontSize = resolveItemFontSize(item)
  const fontFamily = resolveItemFontFamily(item)
  const fontWeight = resolveItemFontWeight(item)
  const lineHeight = item.lineHeight ?? Math.max(fontSize * 1.35, fontSize + 4)
  const padding = item.padding ?? 0
  const textAlign = item.textAlign || (String(item.style?.textAlign || 'left') as CanvasTextAlign)
  const verticalAlign = item.verticalAlign || 'top'
  const lines = text.split('\n')

  ctx.save()
  ctx.fillStyle = resolveItemTextColor(item)
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
  ctx.textAlign = textAlign
  ctx.textBaseline = 'top'

  let drawX = x + padding
  if (textAlign === 'center') drawX = x + (width / 2)
  if (textAlign === 'right' || textAlign === 'end') drawX = x + width - padding

  const contentHeight = Math.max(lineHeight, lines.length * lineHeight)
  let drawY = y + padding
  if (verticalAlign === 'center') {
    drawY = y + Math.max(0, (height - contentHeight) / 2)
  } else if (verticalAlign === 'bottom') {
    drawY = y + Math.max(0, height - contentHeight - padding)
  }

  const maxWidth = width > 0 ? Math.max(0, width - (padding * 2)) : undefined
  for (const [index, line] of lines.entries()) {
    ctx.fillText(line, drawX, drawY + (index * lineHeight), maxWidth)
  }
  ctx.restore()
}

function drawRectItem(ctx: CanvasRenderingContext2D, item: PreviewDrawItem) {
  const { x, y, width, height } = resolveItemFrame(item)
  const fillColor = item.fillColor || String(item.style?.background || item.style?.backgroundColor || '#365882')
  const strokeColor = item.strokeColor || String(item.style?.borderColor || '#6fb6ff')
  const lineWidth = item.lineWidth ?? 1
  ctx.save()
  ctx.fillStyle = fillColor
  ctx.fillRect(x, y, width, height)
  if (strokeColor && strokeColor !== 'transparent') {
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = lineWidth
    if (item.dash?.length) ctx.setLineDash(item.dash)
    ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, width - 1), Math.max(0, height - 1))
  }
  ctx.restore()
  if (item.text) {
    drawMultilineText(ctx, {
      ...item,
      kind: 'text',
      textAlign: item.textAlign || 'left',
      verticalAlign: item.verticalAlign || 'top',
      padding: item.padding ?? 6,
    })
  }
}

function drawImageItem(ctx: CanvasRenderingContext2D, item: PreviewDrawItem) {
  const { x, y, width, height } = resolveItemFrame(item)
  const url = normalizeUrl(item.imageUrl || '')
  const image = maybeLoadImage(item.key, url, item.onImageLoad)
  if (image && width > 0 && height > 0) {
    ctx.drawImage(image, x, y, width, height)
    return
  }
  if (item.placeholderText && width > 0 && height > 0) {
    drawPlaceholder(ctx, item, item.placeholderText)
  }
}

function renderCanvas() {
  const canvas = canvasRef.value
  if (!canvas || props.designWidth <= 0 || props.designHeight <= 0) return
  const dpr = window.devicePixelRatio || 1
  const scaleX = stageDisplaySize.value.width > 0 ? stageDisplaySize.value.width / props.designWidth : 1
  const scaleY = stageDisplaySize.value.height > 0 ? stageDisplaySize.value.height / props.designHeight : 1
  const renderScale = Math.max(1, Math.min(scaleX, scaleY))
  const bufferWidth = Math.max(1, Math.round(props.designWidth * dpr * renderScale))
  const bufferHeight = Math.max(1, Math.round(props.designHeight * dpr * renderScale))
  if (canvas.width !== bufferWidth) canvas.width = bufferWidth
  if (canvas.height !== bufferHeight) canvas.height = bufferHeight
  canvas.style.width = `${props.designWidth}px`
  canvas.style.height = `${props.designHeight}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.scale(dpr * renderScale, dpr * renderScale)
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, props.designWidth, props.designHeight)

  const backgroundUrl = normalizeUrl(props.backgroundUrl)
  if (backgroundUrl) {
    const background = maybeLoadImage('background', backgroundUrl)
    if (background) {
      ctx.drawImage(background, 0, 0, props.designWidth, props.designHeight)
    }
  } else if (props.backgroundEmptyText) {
    ctx.save()
    ctx.fillStyle = '#111827'
    ctx.fillRect(0, 0, props.designWidth, props.designHeight)
    ctx.fillStyle = '#94a3b8'
    ctx.font = '500 18px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(props.backgroundEmptyText, props.designWidth / 2, props.designHeight / 2, Math.max(0, props.designWidth - 32))
    ctx.restore()
  }

  for (const item of props.drawItems) {
    if (item.kind === 'image') {
      drawImageItem(ctx, item)
      continue
    }
    if (item.kind === 'rect') {
      drawRectItem(ctx, item)
      continue
    }
    drawMultilineText(ctx, item)
  }
}

function handleHotspotPointerdown(item: PreviewHotspotItem, event: PointerEvent) {
  activeHotspotKey.value = item.key
  if (item.stopPointerDown !== false) event.stopPropagation()
  if (item.preventPointerDown !== false) event.preventDefault()
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.focus()
  }
  item.onPointerdown?.(event)
}

function handleResizePointerdown(item: PreviewHotspotItem, handle: NonNullable<PreviewHotspotItem['resizeHandle']>, event: PointerEvent) {
  activeHotspotKey.value = item.key
  if (handle.stopPointerDown !== false) event.stopPropagation()
  if (handle.preventPointerDown !== false) event.preventDefault()
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.parentElement?.focus()
  }
  handle.onPointerdown?.(event)
}

function getArrowDelta(event: KeyboardEvent) {
  if (event.key === 'ArrowLeft') return { dx: -1, dy: 0, dw: -1, dh: 0 }
  if (event.key === 'ArrowRight') return { dx: 1, dy: 0, dw: 1, dh: 0 }
  if (event.key === 'ArrowUp') return { dx: 0, dy: -1, dw: 0, dh: -1 }
  if (event.key === 'ArrowDown') return { dx: 0, dy: 1, dw: 0, dh: 1 }
  return null
}

function handleHotspotKeydown(item: PreviewHotspotItem, event: KeyboardEvent) {
  const delta = getArrowDelta(event)
  if (!delta) return
  if (event.ctrlKey || event.metaKey) {
    if (!item.onKeyboardResize) return
    event.preventDefault()
    event.stopPropagation()
    item.onKeyboardResize({ dw: delta.dw, dh: delta.dh })
    return
  }
  if (!item.onKeyboardMove) return
  event.preventDefault()
  event.stopPropagation()
  item.onKeyboardMove({ dx: delta.dx, dy: delta.dy })
}

function handleStageResize(size: { width: number; height: number }) {
  stageDisplaySize.value = size
  emit('stageResize', size)
  scheduleRender()
}

watch(
  () => [props.designWidth, props.designHeight, props.backgroundUrl, props.backgroundEmptyText, props.drawItems, stageDisplaySize.value.width, stageDisplaySize.value.height],
  () => {
    scheduleRender()
  },
)

onMounted(() => {
  scheduleRender()
})

onBeforeUnmount(() => {
  if (renderFrameHandle) {
    cancelAnimationFrame(renderFrameHandle)
    renderFrameHandle = 0
  }
})
</script>

<template>
  <PreviewSurface
    :host-class="hostClass"
    :stage-class="stageClass"
    :design-width="designWidth"
    :design-height="designHeight"
    :scale-multiplier="scaleMultiplier"
    :zoomable="zoomable"
    :zoom-step="zoomStep"
    fit-mode="transform-stage"
    @stage-resize="handleStageResize"
  >
    <canvas ref="canvasRef" class="canvas-preview-surface__canvas" aria-hidden="true" />
    <PreviewGuideOverlay
      :visible="guideActive && activeGuideCoordinate !== null"
      :design-width="designWidth"
      :design-height="designHeight"
      :surface-width="designWidth"
      :surface-height="designHeight"
      :stage-scale="stageScale"
      :coordinate="activeGuideCoordinate"
      :show-grid="false"
      :label-scale="1.35"
    />
    <component
      :is="item.tag || 'div'"
      v-for="item in hotspotItems"
      :key="item.key"
      :class="[
        'canvas-preview-surface__hotspot',
        item.class,
        item.dragging ? item.draggingClass : null,
        activeHotspotKey === item.key ? 'canvas-preview-surface__hotspot--active' : null,
      ]"
      :style="item.style"
      :type="item.tag === 'button' ? 'button' : undefined"
      :aria-label="item.ariaLabel"
      draggable="false"
      tabindex="0"
      @focus="activeHotspotKey = item.key"
      @keydown="handleHotspotKeydown(item, $event)"
      @pointerdown="handleHotspotPointerdown(item, $event)"
    >
      <component
        :is="item.resizeHandle?.tag || 'button'"
        v-if="item.resizeHandle"
        :class="item.resizeHandle.class"
        :style="item.resizeHandle.style"
        :type="item.resizeHandle?.tag === 'button' ? 'button' : undefined"
        :aria-label="item.resizeHandle.ariaLabel"
        draggable="false"
        @pointerdown="handleResizePointerdown(item, item.resizeHandle, $event)"
      />
    </component>
  </PreviewSurface>
</template>

<style scoped>
.canvas-preview-surface__canvas {
  position: absolute;
  inset: 0;
  display: block;
  pointer-events: none;
}

.canvas-preview-surface__hotspot {
  position: absolute;
  background: transparent !important;
  color: transparent !important;
  box-sizing: border-box;
  user-select: none;
  -webkit-user-drag: none;
  touch-action: none;
  cursor: move;
  outline: 1px dashed transparent;
  outline-offset: 0;
  transition: outline-color 120ms ease, box-shadow 120ms ease;
}

.canvas-preview-surface__hotspot:hover {
  outline-color: rgba(96, 165, 250, 0.75);
}

.canvas-preview-surface__hotspot:focus-visible,
.canvas-preview-surface__hotspot--active {
  outline: 2px solid rgba(59, 130, 246, 0.95);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6);
}

.canvas-preview-surface__hotspot > * {
  -webkit-user-drag: none;
}
</style>
